<template>
  <div class="zpk">
    <!-- Toolbar: search -->
    <div class="zpk__toolbar">
      <Input
        v-model="search"
        search
        clearable
        :placeholder="$t('Search a zone')"
        class="zpk__search"
      />
    </div>

    <!-- Selection summary -->
    <div class="zpk__summary">
      <button
        type="button"
        class="zpk__selected-count"
        :class="{ 'zpk__selected-count--clickable': value.length }"
        :disabled="!value.length"
        @click="openSelected"
      >
        <Icon type="md-checkmark-circle" />
        {{ value.length }} {{ $t("selected") }}
        <Icon
          v-if="value.length"
          type="ios-eye"
          class="zpk__selected-eye"
        />
      </button>
      <button
        v-if="value.length"
        type="button"
        class="zpk__clear"
        @click="clearAll"
      >
        {{ $t("Clear all") }}
      </button>
    </div>

    <!-- Zone cards grid -->
    <div class="zpk__grid">
      <div
        v-for="z in visibleZones"
        :key="z.id"
        class="zpk__zone"
        :class="{ 'zpk__zone--on': isSelected(z.id) }"
        @click="toggleZone(z.id)"
      >
        <div class="zpk__zone-head">
          <input
            type="checkbox"
            class="zpk__cb"
            :checked="isSelected(z.id)"
            @click.stop="toggleZone(z.id)"
          >
          <span class="zpk__zone-name">{{ z.name }}</span>
        </div>
        <div class="zpk__zone-meta">
          <span
            v-if="z.entity"
            class="zpk__zone-entity"
          >{{ organizationStore.entityNameById(z.entity) }}</span>
          <span class="zpk__zone-count">
            {{ (z.countries || []).length }} {{ $t("countries") }}
          </span>
        </div>
        <div
          v-if="(z.countries || []).length"
          class="zpk__flags"
        >
          <img
            v-for="c in flagPreview(z.countries)"
            :key="z.id + '-' + c.iso"
            :src="c.flag"
            :alt="c.display_name"
            :title="c.display_name"
            class="zpk__flag"
          >
          <span
            v-if="(z.countries || []).length > FLAG_PREVIEW"
            class="zpk__flag-more"
          >+{{ (z.countries || []).length - FLAG_PREVIEW }}</span>
        </div>
      </div>
      <div
        v-if="!visibleZones.length"
        class="zpk__empty"
      >
        {{ $t("No zone found") }}
      </div>
    </div>

    <!-- Selected zones modal -->
    <Modal
      v-model="showSelected"
      :title="$t('Selected zones') + ' (' + value.length + ')'"
      width="520"
      footer-hide
    >
      <Input
        v-model="selSearch"
        search
        clearable
        :placeholder="$t('Search a zone')"
        class="zpk__modal-search"
      />
      <ul class="zpk__sel-list">
        <li
          v-for="z in filteredSelectedZones"
          :key="'sel-' + z.id"
          class="zpk__sel-item"
        >
          <span class="zpk__sel-name">{{ z.name }}</span>
          <span class="zpk__sel-count">{{ (z.countries || []).length }} {{ $t("countries") }}</span>
          <button
            type="button"
            class="zpk__sel-remove"
            @click="toggleZone(z.id)"
          >
            <Icon type="md-close" />
          </button>
        </li>
        <li
          v-if="!filteredSelectedZones.length"
          class="zpk__empty"
        >
          {{ $t("No zone found") }}
        </li>
      </ul>
    </Modal>
  </div>
</template>

<script>
export default {
  name: "ZonePicker",
  props: {
    // v-model: array of selected zone ids.
    value: { type: Array, default: () => [] },
  },
  data() {
    return { search: "", showSelected: false, selSearch: "", FLAG_PREVIEW: 6 };
  },
  computed: {
    zones() {
      return this.organizationStore.zones || [];
    },
    visibleZones() {
      const q = this.search.trim().toLowerCase();
      let list = this.zones;
      if (q) {
        list = list.filter((z) => (z.name || "").toLowerCase().includes(q));
      }
      return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    selectedSet() {
      return new Set(this.value);
    },
    selectedZones() {
      const set = this.selectedSet;
      return this.zones
        .filter((z) => set.has(z.id))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    filteredSelectedZones() {
      const q = this.selSearch.trim().toLowerCase();
      if (!q) return this.selectedZones;
      return this.selectedZones.filter((z) => (z.name || "").toLowerCase().includes(q));
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedSet.has(id);
    },
    emit(next) {
      this.$emit("input", next);
    },
    toggleZone(id) {
      const next = new Set(this.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.emit([...next]);
    },
    clearAll() {
      this.emit([]);
    },
    openSelected() {
      if (!this.value.length) return;
      this.selSearch = "";
      this.showSelected = true;
    },
    countryByIso(iso) {
      return (this.worldInfoStore.countryList || []).find((c) => c.iso === iso) || null;
    },
    flagPreview(countries) {
      return (countries || [])
        .map((iso) => this.countryByIso(iso))
        .filter(Boolean)
        .slice(0, this.FLAG_PREVIEW);
    },
  },
};
</script>

<style scoped>
.zpk {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Toolbar */
.zpk__toolbar {
  display: flex;
}
.zpk__search {
  max-width: 320px;
}

/* Summary */
.zpk__summary {
  display: flex;
  align-items: center;
  gap: 12px;
}
.zpk__selected-count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: #f2f4f8;
  color: #515a6e;
  border-radius: 14px;
  padding: 3px 12px;
  font-size: 13px;
  cursor: default;
}
.zpk__selected-count--clickable {
  cursor: pointer;
  background: #e8f4ff;
  color: #2d8cf0;
}
.zpk__selected-eye {
  opacity: 0.7;
}
.zpk__clear {
  border: none;
  background: none;
  color: #c0392b;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
}

/* Grid of zone cards */
.zpk__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
  max-height: 340px;
  overflow-y: auto;
  padding: 2px;
}
.zpk__zone {
  border: 1px solid #e8eaec;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  background: #fff;
}
.zpk__zone:hover {
  border-color: #b3d8ff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.zpk__zone--on {
  border-color: #2d8cf0;
  background: #f0f8ff;
}
.zpk__zone-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.zpk__cb {
  cursor: pointer;
}
.zpk__zone-name {
  font-weight: 600;
  color: #17233d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.zpk__zone-meta {
  display: flex;
  gap: 8px;
  margin: 6px 0 8px;
  font-size: 12px;
  color: #808695;
}
.zpk__zone-entity {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.zpk__zone-count {
  margin-left: auto;
  white-space: nowrap;
}
.zpk__flags {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-wrap: wrap;
}
.zpk__flag {
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.3);
}
.zpk__flag-more {
  font-size: 11px;
  color: #808695;
}

/* Modal selected list */
.zpk__modal-search {
  margin-bottom: 10px;
}
.zpk__sel-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 300px;
  overflow-y: auto;
}
.zpk__sel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 4px;
  border-bottom: 1px solid #f0f0f0;
}
.zpk__sel-name {
  font-weight: 500;
  color: #17233d;
}
.zpk__sel-count {
  margin-left: auto;
  font-size: 12px;
  color: #808695;
}
.zpk__sel-remove {
  border: none;
  background: none;
  color: #c0392b;
  cursor: pointer;
}
.zpk__empty {
  color: #808695;
  font-size: 13px;
  padding: 12px;
  text-align: center;
  grid-column: 1 / -1;
}
</style>
